import { MapControls, OrthographicCamera } from "@react-three/drei"
import { useFrame } from "@react-three/fiber"
import { useEffect, useMemo, useRef } from "react"
import * as THREE from "three"
import type { MapControls as ThreeMapControls } from "three/examples/jsm/controls/MapControls"
import { lerp } from "three/src/math/MathUtils.js"

import { is_narrow_screen, is_touch_screen } from "../../utils/screen_type"
import { CONSTANTS } from "./constants"
import { DebugMapEdges } from "./utils/DebugMapEdges"


const { CELL_SIZE, GRID_SIZE } = CONSTANTS

// Pan boundary: allow the camera target to roam 25% beyond each edge of the grid,
// so the user can always see most of the map but can nudge to the edges.
const GRID_W = GRID_SIZE.x * CELL_SIZE
const GRID_D = GRID_SIZE.y * CELL_SIZE
const FUDGE_MIN = (is_touch_screen() ? 6 : 3) * CELL_SIZE
const FUDGE_MAX = (is_touch_screen() ? 6 : 6) * CELL_SIZE
const MARGIN_ZOOM_1 = -0.16
const MARGIN_ZOOM_9 = 0.04
const PAN_MARGIN = (zoom: number) => GRID_W * lerp(MARGIN_ZOOM_1, MARGIN_ZOOM_9, zoom / 9)
const PAN_MIN_X = (zoom: number) => -PAN_MARGIN(zoom) - FUDGE_MIN
const PAN_MAX_X = (zoom: number) => GRID_W + PAN_MARGIN(zoom) - FUDGE_MAX
const PAN_MIN_Z = (zoom: number) => -PAN_MARGIN(zoom) - FUDGE_MIN
const PAN_MAX_Z = (zoom: number) => GRID_D + PAN_MARGIN(zoom) - FUDGE_MAX


interface CameraAngle
{
    yaw: number
    pitch: number
    roll?: never
}
let CAMERA_ANGLE: CameraAngle
CAMERA_ANGLE = { yaw: 89, pitch: 89 } // direct down
CAMERA_ANGLE = { yaw: 45, pitch: 35 } // isometric
CAMERA_ANGLE = { yaw: 55, pitch: 45 } // partial

/**
 * Orthographic camera positioned at the classic isometric angle (45° yaw, ~35° pitch)
 * so the tile grid fills the view. Adjust `zoom` to scale the view, or replace with
 * OrbitControls + target for interactive panning/zooming.
 * Generated by Claude Sonnet 4.6 - 2026-04-09
 */
interface IsoCameraProps
{
    grid_size: { x: number, y: number }
    cell_size: number
}
export function IsoCamera({ grid_size, cell_size }: IsoCameraProps)
{
    const controls_ref = useRef<ThreeMapControls>(null)
    // const angle = get_app_state(s => s.view.angle)

    const { initial_target, initial_position, initial_zoom, dist } = useMemo(() =>
    {
        // The tiles start at origin 0,0,0 so center the camera on the middle
        // lower part of the grid, i.e. divide by 3 not 2.
        const x = grid_size.x * cell_size / 3
        const z = grid_size.y * cell_size / 3
        const initial_target = new THREE.Vector3(x, 0, z)

        // Distance sized so the whole grid fits in view; (1,1,1) direction = iso angle.
        const dist = Math.max(grid_size.x, grid_size.y) * cell_size * 1.5

        // Compute camera initial position from CAMERA_ANGLE (yaw, pitch in degrees).
        const yaw_rad = THREE.MathUtils.degToRad(CAMERA_ANGLE.yaw)
        const pitch_rad = THREE.MathUtils.degToRad(CAMERA_ANGLE.pitch)
        // Direction vector from spherical coordinates: pitch = elevation from horizontal.
        const dir = new THREE.Vector3(
            Math.cos(pitch_rad) * Math.cos(yaw_rad),
            Math.sin(pitch_rad),
            Math.cos(pitch_rad) * Math.sin(yaw_rad),
        ).normalize()
        // Scale to keep similar framing as previous hard-coded position.
        const DIST_SCALE = 1.65
        const initial_position = initial_target.clone().add(dir.multiplyScalar(dist * DIST_SCALE))

        const initial_zoom = is_narrow_screen() ? 2 : 3

        return { initial_target, initial_position, initial_zoom, dist }
    }, [])

    const cam_ref = useRef<THREE.OrthographicCamera>(null)


    // Ensure middle mouse pans (instead of dolly) and
    // prevent browser auto-scroll on middle-button mousedown over the canvas.
    useEffect(() =>
    {
        const controls = controls_ref.current
        if (controls && controls.mouseButtons)
        {
            controls.mouseButtons.MIDDLE = THREE.MOUSE.PAN
        }

        const on_mouse_down = (e: MouseEvent) =>
        {
            // button === 1 is middle button
            if (e.button === 1)
            {
                // If mousedown happened on the renderer canvas, prevent autoscroll
                const tgt = e.target as HTMLElement | null
                if (tgt && tgt.tagName === "CANVAS") e.preventDefault()
            }
        }

        window.addEventListener("mousedown", on_mouse_down, { passive: false })
        return () => window.removeEventListener("mousedown", on_mouse_down)
    }, [])


    useFrame(() =>
    {
        // Clamp camera target to pan bounds every frame (runs after controls update at priority 1).
        const ctrl = controls_ref.current
        if (!ctrl) return

        const { target, object: camera } = ctrl
        const zoom = (camera as any).zoom as number
        const clamp_x = Math.max(PAN_MIN_X(zoom), Math.min(PAN_MAX_X(zoom), target.x))
        const clamp_z = Math.max(PAN_MIN_Z(zoom), Math.min(PAN_MAX_Z(zoom), target.z))
        if (clamp_x !== target.x || clamp_z !== target.z)
        {
            const dx = clamp_x - target.x
            const dz = clamp_z - target.z
            target.x = clamp_x
            target.z = clamp_z
            camera.position.x += dx
            camera.position.z += dz
        }
    })


    return <>
        {/* Add debug sphere at origin
        <mesh position={[0, 0, 0]}>
            <sphereGeometry args={[5, 16, 16]} />
            <meshBasicMaterial color="red" />
        </mesh> */}
        <OrthographicCamera
            ref={cam_ref}
            makeDefault
            position={initial_position}
            zoom={initial_zoom}
            near={-dist * 4}
            far={dist * 4}
            // lookAt - use MapControls target
        />
        <MapControls
            ref={controls_ref as any}
            // This is the lookAt point for the camera
            target={initial_target}
            makeDefault
            enableRotate={false}
            minZoom={is_narrow_screen() ? 1 : 3}
            maxZoom={15}
            dampingFactor={0.2}
            zoomSpeed={is_touch_screen() ? 1 : 0.7}
            panSpeed={is_touch_screen() ? 1.3 : 1}
        />
        <DebugMapEdges
            visible={false}
            controls_ref={controls_ref as any}
            PAN_MIN_X={PAN_MIN_X}
            PAN_MAX_X={PAN_MAX_X}
            PAN_MIN_Z={PAN_MIN_Z}
            PAN_MAX_Z={PAN_MAX_Z}
        />
    </>
}
